// Agent prompt-version API: list, getActive, getDraft, publish, activate, delete.
// See react-rewrite-plan.md §11.3 (api-entities-settings).

import type { ApiClient } from '@/shared/api';
import { ApiError } from '@/shared/api';
import {
  toAgentPromptVersion,
  toAgentPromptVersionList,
} from '../model/toAgentPromptView';
import type { AgentPromptVersionView } from '../model/types';

export interface PublishPromptInput {
  content: string;
  changeSummary?: string;
  activate?: boolean;
}

export interface AgentPromptApi {
  listVersions(agentId: string, signal?: AbortSignal): Promise<AgentPromptVersionView[]>;
  getActive(agentId: string, signal?: AbortSignal): Promise<AgentPromptVersionView | null>;
  getDraft(agentId: string, signal?: AbortSignal): Promise<AgentPromptVersionView | null>;
  publish(
    agentId: string,
    input: PublishPromptInput,
    signal?: AbortSignal,
  ): Promise<AgentPromptVersionView>;
  activate(
    agentId: string,
    versionId: string,
    signal?: AbortSignal,
  ): Promise<AgentPromptVersionView>;
  deleteVersion(agentId: string, versionId: string, signal?: AbortSignal): Promise<void>;
}

export class AgentPromptMutationError extends ApiError {
  constructor(message: string) {
    super({
      userMessage: 'The agent prompt change could not be completed. Please try again.',
      devMessage: message,
    });
    this.name = 'AgentPromptMutationError';
  }
}

function promptRoot(agentId: string): string {
  return `/api/v1/owner/agents/${encodeURIComponent(agentId)}/prompt`;
}

export function createAgentPromptApi(client: ApiClient): AgentPromptApi {
  return {
    async listVersions(agentId, signal) {
      const raw = await client.request({
        path: `${promptRoot(agentId)}/versions`,
        signal,
      });
      return toAgentPromptVersionList(raw);
    },

    async getActive(agentId, signal) {
      try {
        const raw = await client.request({
          path: `${promptRoot(agentId)}/active`,
          signal,
        });
        return toAgentPromptVersion(raw);
      } catch {
        return null;
      }
    },

    async getDraft(agentId, signal) {
      const versions = await this.listVersions(agentId, signal);
      return versions.find((v) => !v.isActive) ?? null;
    },

    async publish(agentId, input, signal) {
      const activate = input.activate ?? true;
      const raw = await client.request({
        method: 'POST',
        path: `${promptRoot(agentId)}/versions`,
        query: activate ? undefined : { activate: false },
        body: {
          content: input.content,
          change_summary: input.changeSummary ?? null,
        },
        signal,
      });
      const view = toAgentPromptVersion(raw);
      if (view === null) {
        throw new AgentPromptMutationError('publish returned an unexpected payload');
      }
      return view;
    },

    async activate(agentId, versionId, signal) {
      const raw = await client.request({
        method: 'POST',
        path: `${promptRoot(agentId)}/versions/${encodeURIComponent(versionId)}/activate`,
        signal,
      });
      const view = toAgentPromptVersion(raw);
      if (view === null) {
        throw new AgentPromptMutationError('activate returned an unexpected payload');
      }
      return view;
    },

    async deleteVersion(agentId, versionId, signal) {
      await client.request({
        method: 'DELETE',
        path: `${promptRoot(agentId)}/versions/${encodeURIComponent(versionId)}`,
        signal,
      });
    },
  };
}
